import React, { useCallback } from 'react';
import { IonButton } from '@ionic/react';
import { Vector3 } from 'three';
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib';

interface ViewControlsProps {
  controlsRef?: React.RefObject<OrbitControlsImpl | null>;
  className?: string;
}

const ViewControls: React.FC<ViewControlsProps> = ({ controlsRef, className = '' }) => {
  
  // Predefined camera positions (Blender-style)
  const setViewPosition = useCallback((position: [number, number, number], target: [number, number, number] = [0, 0, 0]) => {
    if (!controlsRef?.current) return;

    // Get the camera from the controls
    const camera = controlsRef.current.object;
    
    // Get current target position (where the board is)
    const currentTarget = controlsRef.current.target.clone();
    
    // Calculate relative position from current target
    const relativePosition = new Vector3(...position).add(currentTarget);
    
    // Set camera position relative to board
    camera.position.copy(relativePosition);
    camera.lookAt(currentTarget);
    
    // Update orbit controls target (keep focused on board)
    controlsRef.current.update();
  }, [controlsRef]);

  // Only show on web platform
  if (process.env.REACT_APP_IS_WEB !== 'true') {
    return null;
  }

  const viewPresets = [
    {
      name: 'N',
      title: 'North View',
      position: [0, 0, 3] as [number, number, number], // Looking towards North (positive Z)
      target: [0, 0, 0] as [number, number, number],
      gridPosition: 'north',
    },
    {
      name: 'W',
      title: 'West View',
      position: [-3, 0, 0] as [number, number, number], // Looking towards West (negative X)
      target: [0, 0, 0] as [number, number, number],
      gridPosition: 'west',
    },
    {
      name: '↑',
      title: 'Up View',
      position: [0, 3, 0] as [number, number, number], // Looking down from above
      target: [0, 0, 0] as [number, number, number],
      gridPosition: 'up',
    },
    {
      name: 'E',
      title: 'East View',
      position: [3, 0, 0] as [number, number, number], // Looking towards East (positive X)
      target: [0, 0, 0] as [number, number, number],
      gridPosition: 'east',
    },
    {
      name: 'S',
      title: 'South View',
      position: [0, 0, -3] as [number, number, number], // Looking towards South (negative Z)
      target: [0, 0, 0] as [number, number, number],
      gridPosition: 'south',
    },
  ];

  return (
    <div className={`view-controls ${className}`}>
      <div className="view-controls-container">
        <div className="view-controls-title">View</div>
        <div className="view-controls-compass">
          {viewPresets.map((preset) => (
            <IonButton
              key={preset.name}
              fill="clear"
              size="small"
              className={`view-control-button view-control-${preset.gridPosition}`}
              onClick={() => setViewPosition(preset.position, preset.target)}
              title={preset.title}
            >
              <div className="view-control-content">
                <div className="view-control-label">{preset.name}</div>
              </div>
            </IonButton>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ViewControls;
